import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import '../backlog/backlog.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Flutter → Flutter Default Settings
//
// Port of ALPACA's XAF form over the SHARED singleton dbo.FlutterDefaultSettings
// (the same row ALPACA reads, so a change here changes ALPACA too — CLAUDE.md §5.0).
// Surfaces BOTH the six top-level behaviour toggles of the Flutter field-data app AND the
// "Default Workflow" table (owner request 2026-10-13): 7 tabs (Station / Sampling / Testing
// / Tab1-4), each with a Caption (<Tab> column), an Enabled flag (<Tab>Select) and a Sort
// order (<Tab>Sort). Legacy rule: a selected tab's Sort must be >= 1 (enforced server-side,
// mirrored here). "Testing" drives the tab captioned "Test" in the app. [View] is a T-SQL
// reserved word (bracketed server-side); the JSON key is `view`.
//
// Service/admin only; audited as FormName "Flutter Default Settings".
// ─────────────────────────────────────────────────────────────────────────────

interface WorkflowRow {
  key: string;      // column prefix: Station/Sampling/Testing/Tab1..Tab4
  label: string;    // display label
  caption: string;  // the <key> column (custom tab name)
  select: boolean;  // the <key>Select bit (enabled)
  sort: number;     // the <key>Sort int (order)
}

interface Cfg {
  oid: string | null;
  detectStation: boolean;
  alternateStation: boolean;
  review: boolean;
  sso: boolean;
  view: boolean;
  offlineMode: boolean;
  workflow: WorkflowRow[];
  exists: boolean;
}

type ToggleKey = 'detectStation' | 'alternateStation' | 'review' | 'sso' | 'view' | 'offlineMode';

// Order mirrors the legacy form: Detect · Alternate · Review · View · SSO · Offline (owner request 2026-10-16).
const TOGGLES: { key: ToggleKey; label: string; hint: string }[] = [
  { key: 'detectStation', label: 'Detect Station', hint: 'Auto-detect the sampling station from device location.' },
  { key: 'alternateStation', label: 'Alternate Station', hint: 'Allow the field user to pick an alternate station.' },
  { key: 'review', label: 'Review', hint: 'Require a review step before a field entry is submitted.' },
  { key: 'view', label: 'View', hint: 'Enable the read-only view mode in the field app.' },
  { key: 'sso', label: 'SSO', hint: 'Sign in to the field app with single sign-on.' },
  { key: 'offlineMode', label: 'Offline Mode', hint: 'Let the field app collect data while offline and sync later.' },
];

// The 7 Default-Workflow tabs (display order) — used for the blank/fallback shape before load.
const WF_TABS: { key: string; label: string }[] = [
  { key: 'Station', label: 'Station' },
  { key: 'Sampling', label: 'Sampling' },
  { key: 'Testing', label: 'Testing' },
  { key: 'Tab1', label: 'Tab 1' },
  { key: 'Tab2', label: 'Tab 2' },
  { key: 'Tab3', label: 'Tab 3' },
  { key: 'Tab4', label: 'Tab 4' },
];
const BLANK_WF: WorkflowRow[] = WF_TABS.map((t) => ({ key: t.key, label: t.label, caption: '', select: false, sort: 0 }));

const BLANK: Cfg = {
  oid: null, detectStation: false, alternateStation: false, review: false,
  sso: false, view: false, offlineMode: false, workflow: BLANK_WF, exists: false,
};

const SORT_MSG = "Set the selected tab's sort order to greater than or equal to 1.";

export default function FlutterDefaultSettingsPage() {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [cfg, setCfg] = useState<Cfg>(BLANK);
  const [form, setForm] = useState<Cfg>(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: Cfg }>('/api/v1/flutter-default-settings');
      if (res.data.success) {
        // Fall back to the 7 blank rows if the API ever omits the workflow.
        const data: Cfg = { ...res.data.data, workflow: res.data.data.workflow ?? BLANK_WF };
        setCfg(data);
        setForm(data);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      notify.error(status === 403
        ? 'Administrator / service access required to view Flutter Default Settings.'
        : 'Could not load Flutter Default Settings.');
    } finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // gate.cancel(revert) so Cancel also leaves edit mode (owner report 2026-10-12).
  const handleCancel = () => gate.cancel(() => setForm(cfg));
  const setToggle = (k: ToggleKey, v: boolean) => setForm((f) => ({ ...f, [k]: v }));
  const setWf = (key: string, patch: Partial<WorkflowRow>) =>
    setForm((f) => ({ ...f, workflow: f.workflow.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const handleSave = () => {
    // Legacy rule, mirrored client-side: a selected tab must have Sort >= 1.
    if (form.workflow.some((r) => r.select && r.sort < 1)) { notify.warning(SORT_MSG); return; }
    setSaving(true);
    void gate.save({
      formName: 'Flutter Default Settings',
      successMessage: 'Flutter Default Settings saved successfully.',
      save: async () => {
        await api.put('/api/v1/flutter-default-settings', {
          detectStation: form.detectStation,
          alternateStation: form.alternateStation,
          review: form.review,
          sso: form.sso,
          view: form.view,
          offlineMode: form.offlineMode,
          workflow: form.workflow.map((r) => ({ key: r.key, caption: r.caption, select: r.select, sort: r.sort })),
        });
      },
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        notify.error(status === 403
          ? 'Administrator / service access required to change Flutter Default Settings.'
          : msg ?? 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  const wfInput = (disabled: boolean): CSSProperties => ({
    width: '100%', height: 26, boxSizing: 'border-box',
    border: '1px solid var(--ai-line)', borderRadius: 0, padding: '0 8px',
    fontSize: 13, color: 'var(--ai-ink)', background: disabled ? 'var(--ai-header-bg, #f6f7f9)' : '#fff',
  });

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Flutter</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Flutter Default Settings</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="set-flutter-defaultsettings" auditFormName="Flutter Default Settings"
              loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Shared with ALPACA — a change here applies to both products.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<EditGateActions gate={gate} saving={saving} disabled={loading}
              onSave={handleSave} onCancel={handleCancel} />}
            onRefresh={() => void load()} refreshing={loading} refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 32px' }}>
            <div style={{ maxWidth: 720 }}>
              {/* ── Behaviour toggles ── compact label-left + checkbox, no description
                   line, legacy-form parity (owner request 2026-10-16). */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {TOGGLES.map((t) => (
                  <label key={t.key} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '4px 4px', minHeight: 26,
                    cursor: ro ? 'default' : 'pointer',
                  }}>
                    <span style={{ width: 150, fontSize: 13, color: 'var(--ai-ink)' }}>{t.label}:</span>
                    <input type="checkbox" checked={form[t.key]} disabled={ro}
                      onChange={(e) => setToggle(t.key, e.target.checked)}
                      style={{ accentColor: 'var(--ai-orange)' }} />
                  </label>
                ))}
              </div>

              {/* ── Default Workflow (Station / Sampling / Testing / Tab1-4) ── */}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ai-ink)', marginBottom: 8 }}>
                  Default Workflow
                </div>
                <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ fontSize: 11.5, color: 'var(--ai-muted)', textAlign: 'left' }}>
                      <th style={{ width: 110, padding: '4px 8px 6px 0', fontWeight: 600 }} />
                      <th style={{ padding: '4px 8px 6px', fontWeight: 600 }}>Caption</th>
                      <th style={{ width: 84, padding: '4px 8px 6px', fontWeight: 600, textAlign: 'center' }}>Enabled</th>
                      <th style={{ width: 110, padding: '4px 8px 6px', fontWeight: 600 }}>Sort</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.workflow.map((r) => {
                      const invalid = r.select && r.sort < 1;
                      return (
                        // No zebra — override the global :where(table tbody tr:nth-child(even))
                        // warm-sand stripe so every workflow row is uniform (owner request 2026-10-13).
                        <tr key={r.key} style={{ background: 'transparent' }}>
                          <td style={{ padding: '3px 8px 3px 0', fontSize: 13, fontWeight: 600, color: 'var(--ai-ink)' }}>
                            {r.label}:
                          </td>
                          <td style={{ padding: '3px 8px' }}>
                            <input type="text" value={r.caption} disabled={ro} maxLength={100}
                              placeholder={r.label}
                              onChange={(e) => setWf(r.key, { caption: e.target.value })}
                              style={wfInput(ro)} />
                          </td>
                          <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={r.select} disabled={ro}
                              onChange={(e) => setWf(r.key, { select: e.target.checked })}
                              style={{ accentColor: 'var(--ai-orange)' }} />
                          </td>
                          <td style={{ padding: '3px 8px' }}>
                            <input type="number" value={r.sort} disabled={ro} min={0}
                              onChange={(e) => setWf(r.key, { sort: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })}
                              title={invalid ? SORT_MSG : undefined}
                              style={{ ...wfInput(ro), width: 90, borderColor: invalid ? 'var(--ai-danger, #d33)' : 'var(--ai-line)' }} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
